import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, Typography, Chip, Button, alpha, useTheme } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { 
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ReferenceLine, ResponsiveContainer } from 'recharts';
import { indicatorsAPI } from '../../services/api';

const DEFAULT_VISIBLE = 15;

const IndicatorImpactGrid = ({ indicatorImpacts }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [indicatorNames, setIndicatorNames] = useState({});
  const [expanded, setExpanded] = useState(false);

  // Fetch indicator names on mount
  useEffect(() => {
    const fetchIndicatorNames = async () => {
      try {
        const response = await indicatorsAPI.getIndicators();
        const indicators = response.data;
        const nameMap = {};
        
        indicators.forEach(ind => {
          nameMap[ind.indicator_id] = ind.indicator_name;
        });
        
        setIndicatorNames(nameMap);
      } catch (error) {
        console.error('Failed to fetch indicator names:', error);
      }
    };
    
    fetchIndicatorNames();
  }, []);

  // Helper function to format indicator names
  const formatIndicatorName = (name) => {
    return name.replace(/_/g, ' ');
  };

  const getImpactColor = (score) => {
    if (score > 0.3) return '#1b5e20';
    if (score > 0.15) return '#2e7d32';
    if (score > 0) return '#4caf50';
    if (score > -0.15) return '#ff9800';
    if (score > -0.3) return '#f44336';
    return '#c62828';
  };

  // Sort and prepare data - show all indicators
  const chartData = useMemo(() => {
    const data = [...indicatorImpacts]
      .sort((a, b) => Math.abs(b.avg_impact_score) - Math.abs(a.avg_impact_score))
      .map(impact => ({
        indicator: impact.indicator,
        displayName: indicatorNames[impact.indicator] || formatIndicatorName(impact.indicator),
        shortName: formatIndicatorName(impact.indicator),
        score: impact.avg_impact_score,
        articles: impact.article_count,
        confidence: impact.avg_confidence,
        positive: impact.sentiment_distribution.positive,
        neutral: impact.sentiment_distribution.neutral,
        negative: impact.sentiment_distribution.negative,
        color: getImpactColor(impact.avg_impact_score)
      }));
    
    console.log('IndicatorImpactGrid chartData:', {
      inputCount: indicatorImpacts.length,
      outputCount: data.length,
      sample: data[0]
    });
    
    return data;
  }, [indicatorImpacts, indicatorNames]);

  const displayData = useMemo(() => {
    if (expanded || chartData.length <= DEFAULT_VISIBLE) return chartData;
    return chartData.slice(0, DEFAULT_VISIBLE);
  }, [chartData, expanded]);

  const hasMore = chartData.length > DEFAULT_VISIBLE;

  // Custom Tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload[0]) return null;
    const data = payload[0].payload;

    return (
      <Card 
        elevation={6}
        sx={{ 
          p: 1.5,
          minWidth: 220,
          bgcolor: '#ffffff',
          border: '2px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} mb={0.75}>
          {data.displayName}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
          ({data.indicator})
        </Typography>
        
        <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
          <Typography variant="body2" fontWeight={700} color={data.score > 0 ? 'success.main' : 'error.main'}>
            {data.score > 0 ? '+' : ''}{data.score.toFixed(2)}
          </Typography>
          <Chip label={`${data.articles} ${t('aiNews.articles')}`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
        </Box>
        
        <Box display="flex" gap={0.5} flexWrap="wrap">
          <Chip 
            label={`${t('aiNews.sentiment.positive')}: ${data.positive}`} 
            size="small" 
            sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha('#4caf50', 0.15), color: '#2e7d32' }} 
          />
          <Chip 
            label={`${t('aiNews.sentiment.neutral')}: ${data.neutral}`} 
            size="small" 
            sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha('#000', 0.06), color: 'text.secondary' }} 
          />
          <Chip 
            label={`${t('aiNews.sentiment.negative')}: ${data.negative}`} 
            size="small" 
            sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha('#f44336', 0.15), color: '#c62828' }} 
          />
        </Box>
      </Card>
    );
  };

  return (
    <Box>
      <Card 
        elevation={0}
        sx={{ 
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.background.default, 1)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
          borderRadius: 3,
          overflow: 'visible'
        }}
      >
        {/* Header with gradient background */}
        <Box 
          sx={{ 
            background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
            px: { xs: 2, md: 3 },
            py: 2.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                }}
              >
                <AssessmentIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: -0.5 }}>
                  {t('aiNews.impactScores')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  {t('aiNews.weeklyAnalysis')}
                </Typography>
              </Box>
            </Box>
            <Chip 
              label={`${chartData.length} ${t('aiNews.indicators')}`}
              sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                fontWeight: 700,
                fontSize: '0.875rem',
                height: 36,
                px: 1,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
              }}
            />
          </Box>
        </Box>

        {/* Chart Content */}
        <Box sx={{ p: { xs: 2, md: 3 }, overflow: 'hidden' }}>
          {chartData.length === 0 ? (
            <Box 
              py={{ xs: 4, md: 6 }}
              textAlign="center"
            >
              <AssessmentIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {t('aiNews.noImpactData')}
              </Typography>
            </Box>
          ) : (
            <Box 
              sx={{ 
                width: '100%',
                height: Math.max(300, displayData.length * 28)
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={displayData} 
                  layout="vertical"
                  margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} horizontal={false} />
                  <XAxis 
                    type="number" 
                    domain={['auto', 'auto']}
                    tickFormatter={(value) => value.toFixed(2)}
                    style={{ fontSize: 11 }}
                    stroke={theme.palette.text.secondary}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="shortName" 
                    width={140}
                    style={{ fontSize: 10, fontWeight: 500 }}
                    stroke={theme.palette.text.secondary}
                    tick={{ fill: theme.palette.text.secondary }}
                  />
                  <Tooltip 
                    content={<CustomTooltip />} 
                    cursor={{ fill: alpha(theme.palette.primary.main, 0.05) }}
                    wrapperStyle={{ zIndex: 1000 }}
                    animationDuration={50}
                    animationEasing="ease-out"
                  />
                  <ReferenceLine x={0} stroke={theme.palette.divider} strokeWidth={2} />
                  <Bar 
                    dataKey="score" 
                    radius={[0, 4, 4, 0]}
                    barSize={16}
                  >
                    {displayData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.color}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* Legend + Expand toggle */}
          {chartData.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pt: 1.5,
                mt: 1
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <TrendingUpIcon sx={{ fontSize: 14, color: theme.palette.success.main }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                    {t('aiNews.impactLegend.positive')}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <TrendingDownIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                    {t('aiNews.impactLegend.negative')}
                  </Typography>
                </Box>
              </Box>
              {hasMore && (
                <Button
                  size="small"
                  onClick={() => setExpanded(!expanded)}
                  endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, color: 'primary.main' }}
                >
                  {expanded ? t('aiNews.showLess', 'Thu gọn') : t('aiNews.showAll', `Xem tất cả ${chartData.length}`)}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default IndicatorImpactGrid;
